import { useMutation } from "@apollo/client"
import { Intent } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomTagInput from "components/Atoms/CustomTagInput"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import { BlockUserNote } from "components/Common/BlockUserNote"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import FormLayout from "components/Molecules/FormLayout"
import MutationOptions from "components/Organisms/MutationOptions"
import FileUploadDialog from "components/Templates/FileUploadDialog"
import { AppPermissions } from "constants/permissions"
import { EDUCATION_TYPES, GENDER_TYPES } from "data/static.items"
import { ADD_FARMER, FARMER_DETAILS, FARMER_LIST } from "graphql/farmer.gql"
import { DELETE_MEDIA } from "graphql/media/media.mutations.gql"
import { BANKS, COMPANIES, GROUPS, KYCS } from "graphql/selector.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import { useAuthorizationSimple } from "hooks/useAuthorizationStrict"
import useErrors from "hooks/useErrors"
import useRouteForm from "hooks/useRouteForm"
import { cloneDeep, get, isEqual, set, unionWith } from "lodash-es"
import farmerModel from "models/organisms/farmer.model"
import React, { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import AppToaster, { ErrorToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import {
  DocumentTypes,
  isInValidDocumentPresent,
  isValidDocument,
} from "utils/documentHelper"
import { remove } from "utils/ds.utils"
import { graphqlError } from "utils/parseError"

const farmerDefault = {
  group: null,
  companies: [],
  kycDetails: [],
  bankDetails: [],
}

function FarmerRegistration() {
  const navigate = useNavigate()
  const [farmerDetails, changeFarmerDetails] = useRouteForm(FARMER_DETAILS, {
    default: farmerDefault,
    transform(state) {
      return {
        ...state,
        education: EDUCATION_TYPES.find(v => v.id === state.education),
        gender: GENDER_TYPES.find(v => v.id === state.gender),
        secondary_number: state?.secondaryNumbers?.map(it => it?.number),
      }
    },
  })
  const [isFileUploadOpen, setUpload] = useState(false)

  const [deleteMedia] = useMutation(DELETE_MEDIA)

  function handleMediaDelete(id) {
    deleteMediaMutation(id)
  }

  function deleteMediaMutation(id) {
    deleteMedia({
      variables: {
        ids: [id],
      },
    })
  }

  const parsedVariables = useMemo(() => {
    const farmerDetailsCopy = cloneDeep(farmerDetails)

    const kyc = farmerDetailsCopy?.kycDetails?.[0]
    const bank = farmerDetailsCopy?.bankDetails?.[0]
    return {
      id: farmerDetailsCopy?.id,
      firstName: farmerDetailsCopy?.firstName,
      lastName: farmerDetailsCopy?.lastName,
      number: farmerDetailsCopy?.number,
      username: farmerDetailsCopy?.username,
      email: farmerDetailsCopy?.email,
      dateOfBirth: farmerDetailsCopy?.dateOfBirth,
      secondaryNumbers: farmerDetailsCopy?.secondary_number?.map(number => {
        return {
          number: number,
        }
      }),
      // referenceId: farmerDetailsCopy?.referenceId,
      gender: farmerDetailsCopy?.gender?.id,
      education: farmerDetailsCopy?.education?.id,
      companies: farmerDetailsCopy?.companies?.map(getId),
      groups: farmerDetailsCopy?.groups?.map(getId),
      kycDetail: {
        id: kyc?.id,
        kycType: kyc?.kycType?.id,
        idNumber: kyc?.idNumber,
        photo: kyc?.photo,
      },
      bankDetail: {
        id: bank?.id,
        accountNumber: bank?.accountNumber,
        ifscCode: bank?.ifscCode,
        accountHolderName: bank?.accountHolderName,
        bank: bank?.bank?.id,
        branchName: bank?.branchName,
        attachments: bank?.attachments?.map(p => ({
          id: p?.id,
          url: p?.url,
          mediaType: p?.mediaType ?? "PHT",
        })),
      },
    }
  }, [farmerDetails])
  const [errors] = useErrors({ schema: farmerModel, state: farmerDetails })

  const handleChange = (path, value) => {
    const farmerDetailsCopy = cloneDeep(farmerDetails)
    if (path === "groups") {
      // This code makes Sure that the internal groups are always present no matter what the user selects or removes
      // by taking a union of internal groups and the user manipulated list (value)
      const externalGroups = farmerDetails?.groups?.filter(
        g => g?.category?.codename !== "EXT"
      )
      set(
        farmerDetailsCopy,
        "groups",
        unionWith(externalGroups, value, isEqual)
      )
    } else {
      set(farmerDetailsCopy, path, value)
    }

    changeFarmerDetails(farmerDetailsCopy)
  }
  const showSuccessToast = data => {
    const farmer = get(data, "addUpdateOnlyFarmer.user")
    AppToaster.show({
      message: `Farmer ${farmer.firstName} ${farmer.lastName} ${
        farmerDetails.id ? "updated" : "added"
      } successfully`,
      intent: "success",
    })
  }

  const disabled = useFormDisabled("supplier", parsedVariables)
  const canEditAlternateNumber = useAuthorizationSimple(
    AppPermissions.canEditAlternateSupplierNumber
  )

  return (
    <FormLayout
      className="vt-user-registration"
      id={farmerDetails.id}
      type="Supplier"
    >
      <>
        {farmerDetails.id && (
          <>
            <div className="flex items-baseline">
              <h5 className="mr-6 text-2xl">
                <span className="text-muted">Farmer - </span>{" "}
                {farmerDetails.firstName} {farmerDetails.lastName}{" "}
                <span className="text-muted">
                  ( {farmerDetails.companyCode} )
                </span>
              </h5>
            </div>
            <hr className="mt-0 mb-3" />
            {farmerDetails.isBlocked && (
              <BlockUserNote remark={farmerDetails.blockRemark} />
            )}
          </>
        )}

        <FormController
          source={farmerDetails}
          onChange={handleChange}
          errors={errors.details}
          disabled={disabled || farmerDetails.isBlocked}
          disableAll={true}
        >
          <div className="grid grid-cols-4 gap-x-4 gap-y-0">
            <StringInput
              label="First Name"
              path="firstName"
              required
            ></StringInput>
            <StringInput
              label="Last Name"
              path="lastName"
              required
            ></StringInput>
            <StringInput
              label="Username"
              helperText="A unique name given to the supplier"
              path="username"
            />
            <StringInput
              label="Phone Number"
              type="text"
              path="number"
              helperText="Permanent number of the supplier"
            ></StringInput>
            <StringInput
              label="E-Mail"
              path="email"
              helperText="xyz@gmail.com"
            ></StringInput>
            <CustomDateInput
              fill={true}
              label="Date Of Birth"
              path="dateOfBirth"
              helperText="eg. 27/06/1985"
            ></CustomDateInput>
            <StaticSelector
              big={true}
              path="gender"
              items={GENDER_TYPES}
              label="Gender"
            />
            <StaticSelector
              big={true}
              path="education"
              items={EDUCATION_TYPES}
              label="Education"
            />
            <DynamicMultiSelector
              query={COMPANIES}
              path="companies"
              big={true}
              fill
              label="Companies"
              required
              disabled={false}
              helperText={"eg. SFPCL"}
              tagProps={name => {
                const company = farmerDetails?.companies?.find(
                  c => c.name === name
                )
                if (company?.kind !== "ZONE") {
                  return null
                } else {
                  return {
                    interactive: false,
                    onRemove: () => {
                      AppToaster.show({
                        intent: "warning",
                        message: "Can't Remove Non-External Companies!",
                      })
                    },
                    minimal: true,
                    intent: "warning",
                  }
                }
              }}
            />
            <DynamicMultiSelector
              required
              formClassName="col-span-3"
              big={true}
              query={GROUPS}
              variables={{
                category: "EXT",
              }}
              path="groups"
              fill={true}
              label="Groups"
              tagProps={name => {
                const group = farmerDetails?.groups?.find(g => g.name === name)

                if (group?.category?.codename === "EXT") {
                  return null
                } else {
                  return {
                    interactive: false,
                    onRemove: () => {
                      AppToaster.show({
                        intent: "warning",
                        message: "Cant Remove Non-External Groups!",
                      })
                    },
                    minimal: true,
                    intent: "warning",
                  }
                }
              }}
            />
            <CustomTagInput
              label="Alternate Mobile Number"
              path="secondary_number"
              labelProps={"col-span-3"}
              disabled={farmerDetails?.id && !canEditAlternateNumber}
              type={"number"}
              regex={/^\d{10}$/}
              errorMessage={"Please enter valid mobile numbers (10 digits)."}
            ></CustomTagInput>
          </div>
          <h6 className="mt-6 vt-section-header">
            KYC Details{" "}
            <CustomButton
              icon="plus"
              intent="success"
              minimal
              onClick={e => {
                changeFarmerDetails(prev => ({
                  ...prev,
                  kycDetails: prev?.kycDetails?.concat({}),
                }))
              }}
            />
          </h6>
          <hr className="mt-0" />
          {farmerDetails?.kycDetails?.map((d, idx) => {
            const pan = farmerDetails?.kycDetails?.find(doc => {
              return doc?.kycType?.name === DocumentTypes.PAN
            })?.idNumber
            return (
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-3">
                  <DynamicSelector
                    big
                    query={KYCS}
                    path={`kycDetails.${idx}.kycType`}
                    label="Type"
                    helperText="eg. Aadhar"
                  />
                </div>
                <div className="col-span-3">
                  <StringInput
                    path={`kycDetails.${idx}.idNumber`}
                    label="Number"
                    helperText="eg. 435678986545"
                    error={
                      isValidDocument({
                        documentKind: d?.kycType?.name,
                        documentNumber: d?.idNumber,
                        pan: pan,
                        state: farmerDetails?.address?.state,
                      }) || ""
                    }
                  />
                </div>

                <div className="col-span-1 flex justify-center items-center">
                  <CustomButton
                    className="mb-2"
                    icon="trash"
                    intent="danger"
                    minimal
                    onClick={e => {
                      changeFarmerDetails(prev => ({
                        ...prev,
                        kycDetails: remove(prev?.kycDetails, idx),
                      }))
                    }}
                  />
                </div>
              </div>
            )
          })}
          <h6 className="mt-6 vt-section-header">Bank Details</h6>
          <hr className="mt-0" />
          <div className="flex flex-col gap-4">
            {farmerDetails?.bankDetails?.map((bank, idx) => {
              const base = `bankDetails.${idx}.`
              return (
                <div className="grid grid-cols-3 gap-x-4 gap-y-0">
                  <StringInput
                    path={base + `accountHolderName`}
                    label="Account Holder"
                    helperText="account holder name"
                  />
                  <StringInput
                    path={base + `accountNumber`}
                    label="Account Number"
                    helperText="eg. 00006746464442352"
                  />
                  <StringInput
                    path={base + `branchName`}
                    label="Branch"
                    helperText="Thatte Nagar"
                  />
                  <DynamicSelector
                    query={BANKS}
                    big={true}
                    label="Bank"
                    helperText="eg. ICICI"
                    path={base + `bank`}
                  />
                  <StringInput
                    path={base + `ifscCode`}
                    label="IFSC Number"
                    helperText="eg. ICIC0006517"
                  />
                  <CustomFormGroup
                    label="Attachments"
                    labelInfo={
                      <span className="vt-highlight vt-dark">
                        ({bank?.attachments?.length || "No"} Photos uploaded)
                      </span>
                    }
                  >
                    <CustomButton
                      intent="primary"
                      fill
                      icon="cloud-upload"
                      text="Upload Photos"
                      onClick={() => setUpload(true)}
                    />
                  </CustomFormGroup>
                  <FileUploadDialog
                    disabled={disabled}
                    onDelete={e => {
                      handleMediaDelete(e?.id)
                      const ownerDetailsCopy = cloneDeep(farmerDetails)
                      const attachments =
                        ownerDetailsCopy?.bankDetails?.[
                          idx
                        ]?.attachments?.filter(att => att?.id !== e?.id) || []
                      set(ownerDetailsCopy, base + ".attachments", attachments)
                      changeFarmerDetails(ownerDetailsCopy)
                    }}
                    path={base + "attachments"}
                    isOpen={isFileUploadOpen}
                    onClose={() => setUpload(false)}
                  />
                </div>
              )
            })}
          </div>
        </FormController>

        <div className="flex justify-end">
          <MutationOptions
            // disabled={disabled}
            mutation={ADD_FARMER}
            variables={{ farmer: parsedVariables }}
            validate={() => {
              const docIsValid = isInValidDocumentPresent({
                documents: farmerDetails?.kycDetails,
                state: farmerDetails?.address?.state,
              })

              if (docIsValid !== undefined && !docIsValid) {
                ErrorToast("Please resolve all errors before proceeding")
                return false
              }

              if (errors.latest) {
                AppToaster.show({
                  message: errors.latest,
                  intent: "danger",
                  icon: "cross",
                })
                return false
              }
              return true
            }}
            refetchQueries={[
              {
                query: FARMER_LIST,
                variables: {
                  group: "Farmer",
                  first: 13,
                  query: "",
                  skip: 0,
                },
              },
            ]}
            onError={error => {
              AppToaster.show({
                message: graphqlError(error.message),
                intent: Intent.DANGER,
              })
            }}
            onSave={data => {
              showSuccessToast(data)
              changeFarmerDetails(farmerDefault)
              navigate(-1)
            }}
            onSaveAndAdd={data => {
              showSuccessToast(data)
              changeFarmerDetails(farmerDefault)
            }}
            onSaveAndEdit={data => {
              showSuccessToast(data)
            }}
          ></MutationOptions>
        </div>
      </>
    </FormLayout>
  )
}

export default FarmerRegistration
